import React from 'react'

import type { IResolvedHost } from '@loc/hosts/src/helpers.server'
import type { IButtonProps } from '@loc/one-ui/dist/components/Button'
import type UAParser from 'ua-parser-js'

import config from '@/config'
import { emptyObject } from '@/constants/react'


import { useEffect, useMemo } from 'react';
export interface IErrorConfig {
  image: JSX.Element
  message?: React.ReactNode
  button?: Omit<IButtonProps, 'children'>
  errorText?: React.ReactNode
  errorButton?: React.ReactNode
}

export interface ISiteConfig {
  /** 站点名称 */
  name: string
  /** SEO 中用到的 title */
  seoTitle: (title: string, divider?: string) => string
  /** 自定义 404 页面相关配置 */
  404: IErrorConfig
  /** 自定义 500 页面相关配置 */
  500: IErrorConfig
  /** 自定义客户端异常页面相关配置，默认图片为所有项目可通用的 */
  clientError?: Partial<IErrorConfig>
  /** 上报相关配置 */
  analytics: {
    gtmId: string
    /** posthog 配置 */
    posthogApiHost?: string
    posthogKey?: string
  }
  /** 用户联系我们的邮箱 */
  supportEmail: string
  /** 是否启用 session provider */
  sessionProviderEnabled?: boolean
}

export const SiteContext = React.createContext<{
  resolvedHost: IResolvedHost
  config: ISiteConfig
  cacheData?: Record<string, any>
  browserInfo: UAParser.IBrowser
}>({
  resolvedHost: null!,
  config: null!,
  cacheData: {},
  browserInfo: null!,
})

export const SiteContextProvider = ({
  resolvedHost,
  cacheData = emptyObject,
  children,
  browserInfo,
}: {
  resolvedHost: IResolvedHost
  cacheData?: Record<string, any>
  children: React.ReactNode
  browserInfo: UAParser.IBrowser
}) => {
  useEffect(() => {
    window._resolvedHost = resolvedHost
  }, [resolvedHost])

  useEffect(() => {
    window._browserInfo = browserInfo
  }, [browserInfo])

  useEffect(() => {
    window._cacheData = {
      ...window._cacheData,
      ...cacheData,
    }
  }, [cacheData])

  const siteContextValue = useMemo(() => {
    return {
      resolvedHost,
      cacheData,
      browserInfo,
      config: (config[resolvedHost.hostKey] ||
        config[resolvedHost.deepHostKey])!,
    }
  }, [browserInfo, cacheData, resolvedHost])

  return (
    <SiteContext.Provider value={siteContextValue}>
      {children}
    </SiteContext.Provider>
  )
}
